import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Plus, MapPin, Calendar as CalendarIcon, List } from 'lucide-react';
import { addSeanceToCalendar } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ParticipantsList from './ParticipantsList';

function ClubCalendar({ club, onSeanceAdded }) {
  const { isAuthenticated, user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSeance, setSelectedSeance] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' ou 'list'

  const moisFrancais = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const joursFrancais = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const joursFrancaisCourt = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getSeancesForDate = (date) => {
    if (!date || !club.seances) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (checkDate < today) {
      return [];
    }
    
    const dayOfWeek = joursFrancais[date.getDay()];
    return club.seances.filter(seance => seance.jour === dayOfWeek);
  };

  const getSeancesForMonth = () => {
    if (!club.seances) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const days = getDaysInMonth(currentDate);
    const seancesWithDates = [];

    days.forEach(date => {
      if (!date) return;
      
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      
      if (checkDate < today) return;
      
      const dayOfWeek = joursFrancais[date.getDay()];
      const seancesForDay = club.seances.filter(seance => seance.jour === dayOfWeek);
      
      seancesForDay.forEach(seance => {
        seancesWithDates.push({
          ...seance,
          date: new Date(date)
        });
      });
    });

    return seancesWithDates.sort((a, b) => a.date - b.date);
  };

  const getTypeSeanceColor = (typeSeance) => {
    const colors = {
      'Endurance Fondamentale': 'bg-green-500',
      'Récupération': 'bg-blue-500',
      'Seuil/Tempo': 'bg-yellow-500',
      'Fractionné Court': 'bg-red-500',
      'Fractionné Long': 'bg-orange-500',
      'Sortie Longue': 'bg-purple-500',
      'Autre': 'bg-gray-500'
    };
    return colors[typeSeance] || 'bg-slate-600';
  };

  const formatPace = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddSeance = async () => {
    if (!isAuthenticated) {
      alert('Vous devez être connecté pour ajouter une séance');
      return;
    }

    try {
      const seanceIndex = club.seances.findIndex(s => s === selectedSeance);
      
      await addSeanceToCalendar(club._id, seanceIndex, selectedSeance, selectedDate.toISOString());
      
      alert('Séance ajoutée à votre calendrier !');
      setShowModal(false);
      if (onSeanceAdded) onSeanceAdded();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'ajout de la séance');
    }
  };

  const openModal = (date, seance) => {
    setSelectedDate(date);
    setSelectedSeance(seance);
    setShowModal(true);
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-3 sm:p-6 mt-4 sm:mt-8">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">Calendrier des séances</h2>
      <div className="bg-slate-800 border border-indigo-500/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border-l-4 border-l-indigo-500">
        <p className="text-sm sm:text-base text-gray-300 flex items-start">
          <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
          <span>Cliquez sur une séance de votre choix pour l'ajouter à votre calendrier</span>
        </p>
      </div>

      {/* Navigation mois et Toggle View */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex items-center space-x-3 w-full sm:w-auto justify-center">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-slate-800 text-gray-300 hover:text-white rounded-lg transition"
            aria-label="Mois précédent"
          >
            <ChevronLeft size={24} />
          </button>
          
          <h3 className="text-base sm:text-xl font-semibold text-white min-w-[180px] sm:min-w-[220px] text-center">
            {moisFrancais[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-slate-800 text-gray-300 hover:text-white rounded-lg transition"
            aria-label="Mois suivant"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Toggle view mode - visible seulement sur mobile */}
        <div className="flex md:hidden bg-slate-800 border border-slate-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex items-center space-x-2 px-3 py-2 rounded transition ${
              viewMode === 'calendar'
                ? 'bg-indigo-500 text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <CalendarIcon size={16} />
            <span className="text-sm font-semibold">Calendrier</span>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center space-x-2 px-3 py-2 rounded transition ${
              viewMode === 'list'
                ? 'bg-indigo-500 text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <List size={16} />
            <span className="text-sm font-semibold">Liste</span>
          </button>
        </div>
      </div>

      {/* Vue Liste (mobile uniquement) */}
      {viewMode === 'list' && (
        <div className="md:hidden space-y-2">
          {getSeancesForMonth().map((seance, idx) => (
            <div
              key={idx}
              onClick={() => openModal(seance.date, seance)}
              className="bg-slate-800 border border-slate-700 hover:border-indigo-500 p-3 rounded-lg cursor-pointer transition"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-white font-semibold">
                    {seance.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                  <p className="text-gray-400 text-sm">{seance.heure}</p>
                </div>
                <span className={`px-2 py-1 rounded text-white text-xs font-semibold ${getTypeSeanceColor(seance.typeSeance)}`}>
                  {seance.typeSeance}
                </span>
              </div>
              {seance.distance && (
                <p className="text-gray-300 text-sm">{seance.distance}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vue Calendrier */}
      {viewMode === 'calendar' && (
        <>
          {/* En-têtes jours */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
            {joursFrancaisCourt.map((jour) => (
              <div key={jour} className="text-center font-semibold text-gray-400 text-xs sm:text-sm py-2">
                {jour}
              </div>
            ))}
          </div>

          {/* Grille calendrier */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {days.map((date, index) => {
              const seancesForDay = date ? getSeancesForDate(date) : [];
              
              return (
                <div
                  key={index}
                  className={`min-h-[70px] sm:min-h-[90px] md:min-h-24 p-1 sm:p-2 border rounded-lg ${
                    !date ? 'bg-slate-950 border-slate-800' :
                    isToday(date) ? 'bg-indigo-900/30 border-indigo-500 border-2' :
                    'bg-slate-800 border-slate-700 hover:border-indigo-500'
                  } transition`}
                >
                  {date && (
                    <>
                      <div className="text-xs sm:text-sm font-semibold text-gray-300 mb-1 text-center sm:text-left">
                        {date.getDate()}
                      </div>
                      
                      {seancesForDay.length > 0 && (
                        <div className="space-y-1">
                          {seancesForDay.map((seance, idx) => (
                            <div key={idx}>
                              <div
                                onClick={() => openModal(date, seance)}
                                className={`${getTypeSeanceColor(seance.typeSeance)} text-white text-xs p-1 sm:p-1.5 rounded cursor-pointer hover:opacity-80 transition`}
                              >
                                {/* Version desktop */}
                                <div className="hidden sm:block">
                                  <div className="flex items-center justify-between mb-1">
                                    <Clock size={10} />
                                    <span className="font-semibold text-xs">{seance.heure}</span>
                                  </div>
                                  <div className="font-semibold text-xs truncate">{seance.typeSeance}</div>
                                </div>

                                {/* Version mobile - juste un point coloré */}
                                <div className="sm:hidden flex items-center justify-center">
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Légende */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-slate-800 border border-slate-700 rounded-lg">
        <h4 className="font-semibold text-sm sm:text-base text-white mb-2 sm:mb-3">Légende des séances</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded flex-shrink-0"></div>
            <span className="text-xs sm:text-sm text-gray-300">Endurance Fondamentale</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-500 rounded flex-shrink-0"></div>
            <span className="text-xs sm:text-sm text-gray-300">Récupération</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-500 rounded flex-shrink-0"></div>
            <span className="text-xs sm:text-sm text-gray-300">Seuil/Tempo</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded flex-shrink-0"></div>
            <span className="text-xs sm:text-sm text-gray-300">Fractionné Court</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-orange-500 rounded flex-shrink-0"></div>
            <span className="text-xs sm:text-sm text-gray-300">Fractionné Long</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded flex-shrink-0"></div>
            <span className="text-xs sm:text-sm text-gray-300">Sortie Longue</span>
          </div>
        </div>
      </div>

      {/* Modal détails séance */}
      {showModal && selectedSeance && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-white">Détails de la séance</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Date</p>
                  <p className="text-base sm:text-lg font-semibold text-white">
                    {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>

                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Heure</p>
                  <p className="text-base sm:text-lg font-semibold text-white">{selectedSeance.heure}</p>
                </div>

                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Type de séance</p>
                  <span className={`inline-block px-3 sm:px-4 py-2 rounded-full text-white text-sm sm:text-base font-semibold ${getTypeSeanceColor(selectedSeance.typeSeance)}`}>
                    {selectedSeance.typeSeance}
                  </span>
                </div>

                {selectedSeance.distance && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Distance</p>
                    <p className="text-base sm:text-lg font-semibold text-white">{selectedSeance.distance}</p>
                  </div>
                )}

                {selectedSeance.paceMin && selectedSeance.paceMax && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Allure cible</p>
                    <p className="text-base sm:text-lg font-semibold text-white">
                      {formatPace(selectedSeance.paceMin)} - {formatPace(selectedSeance.paceMax)} /km
                    </p>
                  </div>
                )}

                {selectedSeance.description && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Description</p>
                    <p className="text-sm sm:text-base text-gray-300">{selectedSeance.description}</p>
                  </div>
                )}

                {selectedSeance.lieu && (
                  <div className="flex items-center text-gray-300 text-sm">
                    <MapPin size={16} className="mr-2 text-indigo-500" />
                    <span>{selectedSeance.lieu}</span>
                  </div>
                )}

                {/* Participants */}
                <div className="pt-4 border-t border-slate-800">
                  <ParticipantsList
                    clubId={club._id}
                    seanceIndex={club.seances.findIndex(s => s === selectedSeance)}
                    date={selectedDate.toISOString()}
                    seance={selectedSeance}
                  />
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full sm:flex-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white py-2.5 sm:py-3 rounded-lg transition font-semibold text-sm sm:text-base"
                >
                  Fermer
                </button>
                {isAuthenticated && club.membres.some(membre => membre._id === user._id) && (
                  <button
                    onClick={handleAddSeance}
                    className="w-full sm:flex-1 flex items-center justify-center space-x-2 bg-indigo-500 hover:bg-indigo-600 text-white py-2.5 sm:py-3 rounded-lg transition font-semibold text-sm sm:text-base shadow-lg shadow-indigo-500/20"
                  >
                    <Plus size={18} />
                    <span>Ajouter à mon calendrier</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClubCalendar;